import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { css } from '@emotion/react';
import { gutterSizes, mediaQuery, breakpoints } from '@gumtree/ui-library/src/base/theme';

import { Pagination, StructuredData } from '@gumtree/ui-library';
import AdSlot from '@gumtree/third-party-ads/src/components/ad-slot';
import { DisplayAdSlotDivNames } from '@gumtree/third-party-ads/src/framework/dfp/ad-config/slot-configs';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import ResultsList from '@gumtree/ui-library/src/results-list';
import { GamSlotsDisplayConfig } from '@gumtree/ui-library/src/results-list/interface';
import { getHashName } from '@gumtree/shared/src/util/browser-service';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import { useFeatureValue } from '@growthbook/growthbook-react';

import {
    cookieValueForDuckDuckGo,
    isDuckDuckGoExtensionActiveInCookie,
    isDuckDuckGoExtensionActiveOnWindow,
    setAdBlockCookie,
} from '@gumtree/shared/src/util/ad-block-util';
import {
    BingBottomSlot,
    BingTopSlot,
} from '@gumtree/third-party-ads/src/framework/bing/client-side/ad-slots';
import type { TextAdConditions } from '@gumtree/third-party-ads/src/framework/util/text-ads-util';
import getGamSlotsConfig from '@gumtree/third-party-ads/src/framework/dfp/ad-config';
import { DeviceTypes } from '@gumtree/shared/src/types/client-data';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { fetchPostAJob } from './get-data/fetch/fetch-post-a-job';
import { updatePostAJob } from './reducers/post-a-job';
import './app.scss';

import { SaveSearch } from './components/save-search/save-search';
import { SortSelector } from './components/sort-selector';
import { LocationFilter } from './components/location-filter/location-filter';
import { FiltersTags } from './components/filters-tags/filters-tags';

import { FiltersOnListView } from './components/filters-list-view';
import { FiltersOnGridView } from './components/filters-grid-view';
import NoSearchResults from './components/noSearchResults';
import type { SrpState } from './reducers';
import { SeoFaq } from './components/seo-faq';
import { LinkRecommender } from './components/link-recommender';
import { getPopulatedSchema, getProductSchema } from './seo-utils';
import { determineGaLabel, determinePageLayout } from './page-layout-util';
import StickyAds from './components/ads/sticky-ads';
import {
    onlyShowOn,
    StyledAppContainer,
    StyledBreadcrumbs,
    StyledFloatingFooter,
    StyledSectionLeftAndMiddle,
    StyledSectionMiddle,
    StyledSectionRight,
    StyledSectionTopAndLeftAndMiddle,
    StyledTBanner,
    StyledSectionTop2,
    StyledSectionTop1,
    rightFullBleedUntilLarge,
} from './app.style';
import InfoMessage from './components/info-message';
import NearbyAdsList from './components/nearby-ads-list';
import KeywordSuggestions from './components/keyword-suggestions';
import KeywordSpellingSuggestion from './components/keyword-spelling-suggestion';
import SeoH1 from './components/seo-h1';
import PostAJob from './components/post-a-job';
import RequestAQuoteForm from './components/request-a-quote-form';

const App = () => {
    const dispatch = useDispatch();
    const {
        breadcrumbs,
        resultsPage: {
            nearbyAds,
            noResultsMessage,
            alternativeKeywordSuggestions,
            isDominantCategoryActive,
            searchAds,
            query,
            adsPagination: { firstPagePath, pagePath, currentPage, numberOfPages },
            product,
            categories: { categoryId, categories } = {},
        },
        baseConfig: {
            category,
            device: { type: deviceType },
            currentPageUrl,
            domain,
            pageType,
            requestId,
            buyerUrl,
        },
        adBlockers,
        postAJob: postAJobRaw = {},
        userData,
    } = useSelector((data: SrpState): SrpState => data);
    const { isVisible = false, qaList = [] } = (postAJobRaw as any) || {};
    // Handle case when user is not logged in (userData is false)
    const userId = userData && typeof userData === 'object' ? userData.id : undefined;

    const pageLayout = determinePageLayout({
        deviceType,
        category,
    });

    const resultSize = searchAds.length + nearbyAds.totalNearbyAdsNumFound;
    const isZeroResults = resultSize === 0;
    const hasSearchQuery = Boolean(query);
    const showPagination = numberOfPages > 1;
    const isMobile = deviceType === DeviceTypes.MOBILE;
    const isBrpPage = pageType === 'ResultsBrowse';
    const newUi =
        !isMobile || (isMobile && (isBrpPage || category.l1Category === 'business-services')); // m的综合搜索列表不用新样式

    const gamSlotsConfig = getGamSlotsConfig({
        pageType,
        resultSize,
        device: deviceType,
        l1Category: category.l1Category,
        l2Category: category.l2Category,
    });

    const gamSlotsDisplayConfigs: GamSlotsDisplayConfig[] = [
        {
            id: DisplayAdSlotDivNames.premiumNative1,
            gridViewPosition: 2,
            listViewPosition: 0,
            isAlwaysFullWidth: false,
        },
        {
            id: DisplayAdSlotDivNames.integratedMpu,
            gridViewPosition: 6,
            listViewPosition: 10,
            isAlwaysFullWidth: true,
        },
        {
            id: DisplayAdSlotDivNames.integratedListing,
            gridViewPosition: 15,
            listViewPosition: 20,
            isAlwaysFullWidth: true,
        },
    ].filter(({ id }) => gamSlotsConfig.find((config) => config.slotId === id));

    const [isTBannerFullBleed, setIsTBannerFullBleed] = useState(false);
    const [isTBanner728pxWide, setIsTBanner728pxWide] = useState(false);
    const [isAdExpired, setIsAdExpired] = useState(false);
    const [isDuckDuckGoBlockingAds, setIsDuckDuckGoBlockingAds] = useState(
        isDuckDuckGoExtensionActiveInCookie(adBlockers)
    );
    const [isRequestAQuoteFormOpen, setIsRequestAQuoteFormOpen] = useState(false);

    const [requestQuoteFormData, setRequestQuoteFormData] = useState({
        profile: '',
        title: 'Fences, gates, post storm cleanups, pirate ships. Building and fixing',
        preferredContactType: 'quote' as 'quote' | 'call',
        sellerId: '123',
        advertId: '123',
        sellerSkills: [] as any[],
    });

    const updateRequestQuoteFormData = {
        setProfile: (profile: string) => setRequestQuoteFormData((prev) => ({ ...prev, profile })),
        setTitle: (title: string) => setRequestQuoteFormData((prev) => ({ ...prev, title })),
        setPreferredContactType: (preferredContactType: 'quote' | 'call') =>
            setRequestQuoteFormData((prev) => ({ ...prev, preferredContactType })),
        setSellerId: (sellerId: string) =>
            setRequestQuoteFormData((prev) => ({ ...prev, sellerId })),
        setAdvertId: (advertId: string) =>
            setRequestQuoteFormData((prev) => ({ ...prev, advertId })),
        setSellerSkills: (sellerSkills: any[]) =>
            setRequestQuoteFormData((prev) => ({ ...prev, sellerSkills })),
        openModal: () => setIsRequestAQuoteFormOpen(true),
    };

    const showAdIsRemovedMessage = currentPageUrl.includes('adRemoved=true');
    const showBreadcrumb = !currentPageUrl.includes('/search?');
    const showPostDateExp = useFeatureValue(GrowthBookFeature.SRP_REMOVE_POSTDATE, 'A');
    useEffect(() => {
        setIsAdExpired(getHashName().includes('#adexpired'));

        const srpIsGridOrList = determineGaLabel(window.innerWidth, pageLayout);
        trackV2(
            'ResultsImpressions',
            `natural=${searchAds.length};nearby=${nearbyAds.ads.length}`,
            {
                uaNonInt: true,
                srpIsGridOrList,
            }
        );

        trackV2(
            isDominantCategoryActive ? 'DominantCategoryFound' : 'DominantCategoryNotFound',
            '',
            {
                uaNonInt: true,
                srpIsGridOrList,
            }
        );

        // Fetch PostAJob data on client side if categoryId exists
        if (categoryId) {
            const { curGT, curBK } = searchAds.reduce(
                (acc, ad) => {
                    if (!ad.adSource) {
                        acc.curGT++;
                    } else if (ad.adSource === 'Bark') {
                        acc.curBK++;
                    }
                    return acc;
                },
                { curGT: 0, curBK: 0 }
            );
            
            fetchPostAJob(categoryId, curGT, curBK)
                .then((postAJobData) => {
                    dispatch(updatePostAJob(postAJobData));
                })
                .catch((error) => {
                    console.error('Error fetching post a job data:', error);
                    // Keep default values if fetch fails
                });
        }

        if (!isDuckDuckGoBlockingAds && isDuckDuckGoExtensionActiveOnWindow(window)) {
            setIsDuckDuckGoBlockingAds(true);
            setAdBlockCookie(window, cookieValueForDuckDuckGo);
        }

        (window as any).googletag.cmd.push(() => {
            (window as any).googletag.pubads().addEventListener(
                'slotRenderEnded',
                (event: {
                    serviceName: 'publisher_ads';
                    slot: {
                        getSlotElementId: () => string;
                    };
                    isEmpty: false;
                    slotContentChanged: true;
                    size: [number, number] | undefined;
                    advertiserId: number;
                    campaignId: number;
                    creativeId: number;
                    creativeTemplateId: null;
                    labelIds: null;
                    lineItemId: number;
                    sourceAgnosticCreativeId: number;
                    sourceAgnosticLineItemId: number;
                    isBackfill: boolean;
                    yieldGroupIds: null;
                    companyIds: null;
                }) => {
                    const slotId = event.slot.getSlotElementId();

                    if (slotId === 'tBanner') {
                        const { size } = event;
                        if (size && size.length === 2) {
                            const [width, height] = size;
                            setIsTBanner728pxWide(width === 728);
                            setIsTBannerFullBleed(width === 2000 && height === 255);
                        }
                    }
                }
            );
        });
    }, [categoryId, searchAds, dispatch]);

    const textAdConditions: TextAdConditions = {
        pageType,
        l1Category: category.l1Category,
        device: deviceType,
        currentPage,
    };

    const srpSchema = getPopulatedSchema(searchAds, domain);
    const productSchema = getProductSchema(product);

    return (
        <>
            <StyledAppContainer isDuckDuckGoBlockingAds={isDuckDuckGoBlockingAds}>
                <StructuredData data={srpSchema} />
                <StructuredData data={productSchema} />
                <StyledTBanner
                    isTBanner728pxWide={isTBanner728pxWide}
                    isTBannerFullBleed={isTBannerFullBleed}
                >
                    <AdSlot
                        css={css`
                            min-height: 90px;
                            height: auto;
                            margin: 0 auto;
                            position: sticky;
                            top: 0;
                        `}
                        id={DisplayAdSlotDivNames.tBanner}
                    />
                </StyledTBanner>
                <StyledSectionTopAndLeftAndMiddle>
                    {isVisible && (
                        <PostAJob
                            qaList={qaList}
                            userId={userId ? userId.toString() : ''}
                            categoryId={categoryId?.toString() || ''}
                            requestId={requestId ? requestId.toString() : ''}
                            currentPageUrl={currentPageUrl}
                            buyerUrl={buyerUrl}
                            breadcrumbs={breadcrumbs.links}
                            categories={categories}
                        />
                    )}
                    <RequestAQuoteForm
                        isModalOpen={isRequestAQuoteFormOpen}
                        requestId={requestId ? requestId.toString() : ''}
                        onCloseModal={() => setIsRequestAQuoteFormOpen(false)}
                        profile={requestQuoteFormData.profile}
                        title={requestQuoteFormData.title}
                        preferredContactType={requestQuoteFormData.preferredContactType}
                        sellerId={requestQuoteFormData.sellerId}
                        sellerName={requestQuoteFormData.title}
                        advertId={requestQuoteFormData.advertId}
                        sellerSkills={requestQuoteFormData.sellerSkills}
                        userId={userId ? userId.toString() : ''}
                        categoryId={categoryId?.toString() || ''}
                        currentPageUrl={currentPageUrl}
                    />
                    <StyledSectionTop1>
                        {showBreadcrumb && <StyledBreadcrumbs breadcrumbs={breadcrumbs} />}
                        <SeoH1 />
                        <KeywordSpellingSuggestion />
                        <KeywordSuggestions
                            alternativeKeywordSuggestions={alternativeKeywordSuggestions}
                            css={rightFullBleedUntilLarge}
                        />
                        <LocationFilter css={onlyShowOn('NON_LARGE_SCREEN')} />
                    </StyledSectionTop1>
                    <StyledSectionTop2>
                        <div
                            css={css`
                                display: flex;
                                align-items: center;
                                justify-content: space-between;
                            `}
                        >
                            <div
                                css={css`
                                    display: flex;
                                `}
                            >
                                <FiltersOnGridView
                                    css={onlyShowOn('NON_LARGE_SCREEN')}
                                    device={deviceType}
                                />
                                <SaveSearch />
                            </div>
                            <div
                                css={css`
                                    display: flex;
                                    gap: 10px;
                                `}
                            >
                                <SortSelector />
                            </div>
                        </div>
                        <FiltersTags css={onlyShowOn('NON_LARGE_SCREEN')} />
                    </StyledSectionTop2>

                    <StyledSectionLeftAndMiddle
                        isZeroResults={isZeroResults}
                        pageLayout={pageLayout}
                    >
                        <FiltersOnListView
                            css={onlyShowOn('LARGE_SCREEN')}
                            {...qaAttribute('section-left')}
                        />
                        <StyledSectionMiddle>
                            <BingTopSlot {...textAdConditions} />
                            <InfoMessage
                                isAdExpired={isAdExpired}
                                showAdIsRemovedMessage={showAdIsRemovedMessage}
                                isZeroResults={isZeroResults}
                                hasSearchQuery={hasSearchQuery}
                                noResultsMessage={noResultsMessage}
                            />
                            <NoSearchResults isZeroResults={isZeroResults} searchQuery={query} />
                            {searchAds.length > 0 && (
                                <ResultsList
                                    ads={searchAds}
                                    pageLayout={pageLayout}
                                    gamSlotsDisplayConfig={gamSlotsDisplayConfigs}
                                    updateRequestQuoteFormData={updateRequestQuoteFormData}
                                    requestId={requestId ? requestId.toString() : ''}
                                    newUi={newUi}
                                    showPostDate={showPostDateExp === 'A'}
                                />
                            )}
                            <NearbyAdsList
                                totalNearbyAdsNumFound={nearbyAds.totalNearbyAdsNumFound}
                                nearbyAds={nearbyAds.ads}
                                pageLayout={pageLayout}
                                updateRequestQuoteFormData={updateRequestQuoteFormData}
                                requestId={requestId ? requestId.toString() : ''}
                                newUi={newUi}
                                showPostDate={showPostDateExp === 'A'}
                            />
                            {showPagination && (
                                <Pagination
                                    currentPage={currentPage}
                                    numberOfPages={numberOfPages}
                                    pagePath={pagePath}
                                    firstPagePath={firstPagePath}
                                    paginationSize={4}
                                    onPageClick={({ pageNumber }) =>
                                        trackV2('PaginationPage', undefined, { pageNumber })
                                    }
                                    onPrevClick={({ pageNumber }) =>
                                        trackV2('PaginationPrev', undefined, { pageNumber })
                                    }
                                    onNextClick={({ pageNumber }) =>
                                        trackV2('PaginationNext', undefined, { pageNumber })
                                    }
                                />
                            )}
                            <LinkRecommender />
                            <BingBottomSlot {...textAdConditions} />
                            <SeoFaq />
                        </StyledSectionMiddle>
                    </StyledSectionLeftAndMiddle>
                </StyledSectionTopAndLeftAndMiddle>
                <StyledSectionRight isTBannerFullBleed={isTBannerFullBleed}>
                    <StickyAds>
                        <AdSlot
                            css={css`
                                margin-bottom: ${gutterSizes.xlarge};
                            `}
                            id={DisplayAdSlotDivNames.rSkyT}
                        />
                        <AdSlot
                            css={css`
                                ${mediaQuery.until(breakpoints.large)} {
                                    display: none;
                                }
                            `}
                            id={DisplayAdSlotDivNames.rSkyT2}
                        />
                    </StickyAds>
                    <StickyAds>
                        <AdSlot
                            css={css`
                                margin-top: ${gutterSizes.xlarge};
                            `}
                            id={DisplayAdSlotDivNames.rSkyB}
                        />
                        <AdSlot
                            css={css`
                                margin-top: ${gutterSizes.xlarge};

                                ${mediaQuery.until(breakpoints.large)} {
                                    display: none;
                                }
                            `}
                            id={DisplayAdSlotDivNames.rSkyB2}
                        />
                    </StickyAds>
                </StyledSectionRight>
            </StyledAppContainer>
            <StyledFloatingFooter>
                <AdSlot
                    css={css`
                        margin: auto;
                    `}
                    id={DisplayAdSlotDivNames.floatingFooter}
                />
            </StyledFloatingFooter>
            <AdSlot
                css={css`
                    height: 0;
                    width: 0;
                `}
                id={DisplayAdSlotDivNames.pixel}
            />
        </>
    );
};

export default App;
